import React, { useContext, useState } from 'react';
import { featuresResolver } from './featuresResolver';
import {
	type ExperimentalProvider,
	type FeaturesDefinition,
	type FeaturesDefinitionBase,
	type ResolvedFeatures,
	type withExperimentalFeaturesHoc,
} from './types';

export const defineExperimentalFeatures = <FeatureDefs extends FeaturesDefinitionBase>(
	definitions: FeaturesDefinition<FeatureDefs>,
) => {
	let ExperimentalContext = React.createContext<ResolvedFeatures<FeatureDefs>>(
		featuresResolver(definitions)({}),
	);

	const ExperimentalProvider: ExperimentalProvider<FeatureDefs> = ({
		value: propValue,
		children,
	}) => {
		// Cache the resolver for each use of the Provider, using the useState() trick.
		// (Can't use useMemo() as it's just a performance hint, not a memoization guarantee)
		const [resolveFeatures] = useState(() => featuresResolver(definitions));

		const resolved = resolveFeatures(propValue);

		return <ExperimentalContext.Provider value={resolved}>{children}</ExperimentalContext.Provider>;
	};

	const useExperimentalFeatures = () => useContext(ExperimentalContext);

	const withExperimentalFeatures: withExperimentalFeaturesHoc<FeatureDefs> =
		(Component) => (props) => (
			<ExperimentalProvider value={props.experimentalFeatures}>
				<Component {...props} />
			</ExperimentalProvider>
		);

	return {
		ExperimentalContext,
		ExperimentalProvider,
		useExperimentalFeatures,
		withExperimentalFeatures,
	};
};
